import { ForwardedRef, forwardRef, Fragment, useMemo } from "react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { Box, BoxProps } from "@chakra-ui/react";
import Select, {
  SelectInstance,
  GroupBase,
  SingleValueProps,
  components,
} from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { groupBy, keyBy } from "lodash";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import useSimpleValue, {
  SimpleValueProps,
} from "components/Shared/Select/useSimpleValue";
import useSelectStyles from "utils/useSelectStyles";

type Option = FilterDefinition;

const UNGROUPED_KEY = "__NONE__";
const KEY_SEPARATOR = "|@|@|";

function getGroupLabelKey(groupLabel: string | string[] | undefined) {
  if (!groupLabel) return UNGROUPED_KEY;
  if (Array.isArray(groupLabel)) return groupLabel.join(KEY_SEPARATOR);
  return groupLabel;
}

function getOptionValue(option: Option) {
  return option.id;
}

type GroupLabelProps = BoxProps & {
  groupLabel: string | string[] | undefined;
};

function GroupLabel({ groupLabel, ...rest }: GroupLabelProps) {
  if (!groupLabel?.length) return null;

  const groupLabelArray = Array.isArray(groupLabel) ? groupLabel : [groupLabel];

  return (
    <Box {...rest}>
      {groupLabelArray.map((part, idx) => (
        <Fragment key={part}>
          {idx !== 0 && (
            <Box
              as="span"
              display="inline-block"
              px=".25em"
              color="gray.400"
              verticalAlign="baseline"
            >
              <ChevronRightIcon boxSize="1.5em" mt="-.3em" />
            </Box>
          )}
          <Box as="span">{part}</Box>
        </Fragment>
      ))}
    </Box>
  );
}

function SingleValue<IsMulti extends boolean = false>({
  children,
  ...rest
}: SingleValueProps<Option, IsMulti>) {
  const { groupLabel } = rest.data;

  if (!groupLabel) {
    return (
      <components.SingleValue {...rest}>{children}</components.SingleValue>
    );
  }

  return (
    <components.SingleValue {...rest}>
      <GroupLabel
        groupLabel={groupLabel}
        fontSize=".7em"
        fontWeight={500}
        color="magenta.dark"
        isTruncated
        lineHeight="normal"
      />
      <Box fontSize=".9em" lineHeight="normal" isTruncated>
        {children}
      </Box>
    </components.SingleValue>
  );
}

function formatGroupLabel(group: GroupBase<Option>) {
  if (!group.label) return null;

  if (group.label === UNGROUPED_KEY) return null;

  const parts = group.label.split(KEY_SEPARATOR);

  return (
    <GroupLabel
      groupLabel={parts}
      fontWeight="bold"
      fontSize="1.15em"
      color="magenta.dark"
    />
  );
}

type Props<IsMulti extends boolean = false> = StateManagerProps<
  Option,
  IsMulti
> &
  SimpleValueProps<string, IsMulti> & {
    filterDefinitions: FilterDefinition[];
  };

function FilterSelect<IsMulti extends boolean = false>(
  {
    isMulti,
    filterDefinitions,
    styles,
    value: rawValue,
    onChange,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles(styles);

  const options = useMemo(() => {
    const visible = filterDefinitions.filter((fd) => !fd.isHidden);
    const grouped = groupBy(visible, (fd) => getGroupLabelKey(fd.groupLabel));
    const groupKeys = Object.keys(grouped);
    groupKeys.sort((a, b) => {
      // Sort ungrouped items to the top
      if (a === UNGROUPED_KEY) return -1;
      if (b === UNGROUPED_KEY) return 1;
      const aIdx = filterDefinitions.findIndex(
        (fd) => getGroupLabelKey(fd.groupLabel) === a,
      );
      const bIdx = filterDefinitions.findIndex(
        (fd) => getGroupLabelKey(fd.groupLabel) === b,
      );
      return aIdx - bIdx;
    });
    return groupKeys.reduce<GroupBase<Option>[]>((acc, groupKey) => {
      const fds = grouped[groupKey];
      acc.push({ label: groupKey, options: fds });
      return acc;
    }, []);
  }, [filterDefinitions]);

  const filterDefinitionsById = useMemo(
    () => keyBy(filterDefinitions, "id"),
    [filterDefinitions],
  );

  const { value, handleChange } = useSimpleValue({
    getOptionFromValue: (filterDefinitionId) =>
      filterDefinitionsById[filterDefinitionId],
    getValueFromOption: (option) => option.id,
    isMulti,
    onChange,
    value: rawValue,
  });

  return (
    <Select
      {...styleProps}
      {...{ ref, getOptionValue, formatGroupLabel, options, value }}
      styles={{
        ...styleProps.styles,
        group: (base) => ({
          ...base,
          borderTopWidth: 1,
          paddingBottom: "1.5em",
          "&:first-of-type": {
            paddingTop: 0,
            borderTopWidth: 0,
          },
          "&:last-of-type": {
            paddingBottom: 0,
          },
        }),
        groupHeading: (base) => ({
          ...base,
          position: "sticky",
          top: -4,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          paddingTop: "1em",
          paddingBottom: "0.5em",
          _empty: { display: "none" },
          "&:empty": { display: "none" },
        }),
        menu: (base) => ({ ...base, overflow: "hidden" }),
        menuList: (base) => ({ ...base, paddingTop: 4 }),
      }}
      components={{ ...styleProps.components, SingleValue }}
      maxMenuHeight={800}
      placeholder="Select filter…"
      onChange={handleChange}
      filterOption={(option, input) =>
        option.label.toString().toLowerCase().includes(input.toLowerCase())
      }
      menuPortalTarget={document.body}
      {...rest}
    />
  );
}

export default forwardRef(FilterSelect);
